/**
 * PackInstaller — landing page for shared genre pack URLs `/pack/:id`.
 *
 * Renders the pack metadata + preview of every genre, with one prominent
 * "Install pack" button that merges the genres into the visitor's localStorage
 * (same dedupe-by-label logic as the SunoModal Import path) then redirects
 * to /dashboard with a toast.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ElpmLogo from "@/components/ElpmLogo";
import axios from "axios";
import { motion } from "framer-motion";
import { Package, Loader2, ArrowLeft, Music4, Check } from "lucide-react";
import { toast } from "sonner";
import { loadCustomGenres, saveCustomGenres } from "@/components/suno/constants";
import { safeApiError } from "@/lib/safeErrorText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PackInstaller() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pack, setPack] = useState(null);
  const [err, setErr] = useState(null);
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/genre-pack/${id}`)
      .then((r) => { if (!cancelled) setPack(r.data); })
      .catch((e) => { if (!cancelled) setErr(safeApiError(e) || "Could not load pack"); });
    return () => { cancelled = true; };
  }, [id]);

  const installPack = async () => {
    if (!pack) return;
    setInstalling(true);
    try {
      const existing = loadCustomGenres();
      const existingLabels = new Set(existing.map((g) => g.label.toLowerCase()));
      const fresh = pack.genres
        .filter((g) => !existingLabels.has(g.label.toLowerCase()))
        .map((g) => ({
          id: `cg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          label: g.label, prompt: g.prompt,
        }));
      const next = [...existing, ...fresh].slice(0, 50);
      saveCustomGenres(next);
      // Best-effort install counter — fire and forget.
      axios.post(`${API}/genre-pack/${id}/install`).catch(() => {});
      const skipped = pack.genres.length - fresh.length;
      setInstalled(true);
      toast.success(
        `Installed ${fresh.length} new genre${fresh.length === 1 ? "" : "s"}` +
        (skipped > 0 ? ` · skipped ${skipped} you already had` : "") +
        ".",
        { description: "Open Make Music → Your Genres to use them." }
      );
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (e) {
      toast.error("Couldn't install the pack — please try again.");
    } finally {
      setInstalling(false);
    }
  };

  if (err) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#0a0814] p-8">
        <div className="text-center">
          <ElpmLogo className="mx-auto mb-6 h-10" testId="pack-error-logo" />
          <Package className="mx-auto h-10 w-10 text-rose-300" />
          <p className="mt-4 font-display text-2xl text-rose-200">Pack not found</p>
          <p className="mt-2 font-mono text-sm text-white/55">{err}</p>
          <Link to="/dashboard" data-testid="pack-back-home" className="mt-6 inline-flex items-center gap-2 rounded-full border border-fuchsia-400/40 bg-fuchsia-500/15 px-5 py-2 font-mono text-xs uppercase tracking-wider text-fuchsia-100 hover:bg-fuchsia-500/25">
            <ArrowLeft className="h-4 w-4" /> Back to studio
          </Link>
        </div>
      </div>
    );
  }

  if (!pack) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#0a0814] p-8">
        <Loader2 className="h-8 w-8 animate-spin text-fuchsia-300" />
      </div>
    );
  }

  return (
    <div
      data-testid="pack-installer"
      className="min-h-screen bg-[#0a0814] bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.18),transparent_55%),radial-gradient(ellipse_at_bottom,rgba(217,70,239,0.10),transparent_55%)] text-white"
    >
      <div className="mx-auto max-w-3xl px-6 py-12">
        <ElpmLogo className="mb-6 h-10" testId="pack-installer-logo" />
        <Link
          to="/dashboard"
          data-testid="pack-home-link"
          className="mb-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 transition hover:text-fuchsia-200"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to studio
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="rounded-3xl border border-emerald-400/25 bg-gradient-to-br from-emerald-500/[0.08] via-white/[0.02] to-fuchsia-500/[0.06] p-8 backdrop-blur-xl"
        >
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-emerald-400/40 to-fuchsia-500/40 ring-1 ring-emerald-300/40">
              <Package className="h-6 w-6 text-white" />
            </span>
            <div>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.22em] text-emerald-200 ring-1 ring-emerald-300/30">
                Genre pack
              </span>
              <h1 data-testid="pack-title" className="mt-1 font-display text-2xl font-semibold leading-tight md:text-3xl">
                {pack.title}
              </h1>
            </div>
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">
            {pack.genres.length} genre{pack.genres.length === 1 ? "" : "s"}
            {pack.author_alias ? ` · by ${pack.author_alias}` : ""}
            {typeof pack.installs === "number" && pack.installs > 0 ? ` · ${pack.installs} installs` : ""}
          </p>

          <div data-testid="pack-genres-container" className="mt-6 space-y-2">
            {pack.genres.map((g, i) => (
              <div
                key={`pg-${i}`}
                data-testid={`pack-genre-${i}`}
                className="rounded-xl border border-white/[0.05] bg-white/[0.02] p-3"
              >
                <p className="font-display text-sm font-semibold text-white">{g.label}</p>
                <p className="mt-1 font-mono text-[11px] leading-5 text-white/55">{g.prompt}</p>
              </div>
            ))}
          </div>

          <button
            type="button"
            data-testid="pack-install-btn"
            onClick={installPack}
            disabled={installing || installed}
            className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-fuchsia-500 px-6 py-3 font-display text-base font-semibold text-white shadow-[0_0_24px_rgba(16,185,129,0.35)] transition hover:opacity-95 disabled:opacity-50"
          >
            {installed ? (
              <><Check className="h-5 w-5" /> Installed — redirecting…</>
            ) : installing ? (
              <><Loader2 className="h-5 w-5 animate-spin" /> Installing…</>
            ) : (
              <><Music4 className="h-5 w-5" /> Install pack to your studio</>
            )}
          </button>
          <p className="mt-3 text-center font-mono text-[9.5px] uppercase tracking-wider text-white/35">
            Genres save to this browser. No account required.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
